import { CallOffOrderColor, FlatQuotationLine, QuotedColor, QuotedColorGroup, SmallLotPricing } from '@ravago/shared/page-data/models/elements';

import { ReadonlyURLSearchParams } from 'next/navigation';

import { DEFAULT_PRODUCER_COLOR_CODE } from '../constants';

import { parseProductActionIntent } from './product-action-url-state';

export const isQuotedColor = (value: CallOffOrderColor | QuotedColor): value is QuotedColor => 'quotation' in value;

export const isCallOffOrderColor = (value: CallOffOrderColor | QuotedColor): value is CallOffOrderColor => 'recommendedCallOff' in value;

interface BracketPricingOptions {
   bracketPricingEnabled?: boolean;
}

type QuotationLine = QuotedColor['quotation']['lines'][number];
type BracketPricing = NonNullable<QuotationLine['bracketPricing']>;

interface ResolvedLineValues {
   quantityAmount: number;
   quantityUOM: string;
   quantityToAmount: number | undefined;
   palletSizeAmount: number | undefined;
   netWeightAmount: number | undefined;
   unitPrice: FlatQuotationLine['lineUnitPrice'];
   packaging: string;
   packagingGroupId: number | undefined;
}

const getBracketPricingNetWeightAmount = (bracketPricing: BracketPricing): number | undefined => bracketPricing.packaging.netWeight?.amount;

const resolveLineValues = (
   line: QuotationLine,
   bracketPricingEnabled: boolean
): ResolvedLineValues => {
   const bracketPricing = bracketPricingEnabled ? line.bracketPricing : undefined;

   if (bracketPricing) {
      return {
         quantityAmount: bracketPricing.quantityFrom.amount,
         quantityUOM: bracketPricing.quantityFrom.unitOfMeasure,
         quantityToAmount: bracketPricing.quantityTo.amount,
         palletSizeAmount: bracketPricing.packaging.palletSize?.amount,
         netWeightAmount: getBracketPricingNetWeightAmount(bracketPricing),
         unitPrice: bracketPricing.unitPrice,
         packaging: bracketPricing.packaging.description,
         packagingGroupId: bracketPricing.packaging.group.id
      };
   }

   return {
      quantityAmount: line.quantity.amount,
      quantityUOM: line.quantity.unitOfMeasure,
      quantityToAmount: undefined,
      palletSizeAmount: undefined,
      netWeightAmount: undefined,
      unitPrice: line.unitPrice,
      packaging: line.packaging,
      packagingGroupId: line.packagingGroupId
   };
};

/** Builds the stable grouping key `{colorId}_{producerColorCode}` used across quoted-color and call-off grouping. */
export const makeColorGroupKey = (colorId: number, producerColorCode: string | undefined): string =>
   `${colorId}_${producerColorCode ?? DEFAULT_PRODUCER_COLOR_CODE}`;

export const groupQuotedColors = (quotedColors: QuotedColor[]): QuotedColorGroup[] => {
   const colorGroupsByKey = new Map<string, QuotedColorGroup>();

   for (const quotedColor of quotedColors) {
      const key = makeColorGroupKey(quotedColor.color.id, quotedColor.color.producerColorCode);

      if (!colorGroupsByKey.has(key)) {
         colorGroupsByKey.set(key, {
            key,
            colorId: quotedColor.color.id,
            colorName: quotedColor.color.name,
            producerColorCode: quotedColor.color.producerColorCode ?? DEFAULT_PRODUCER_COLOR_CODE,
            quotedColors: []
         });
      }

      colorGroupsByKey.get(key)?.quotedColors.push(quotedColor);
   }

   return Array.from(colorGroupsByKey.values());
};

export const flattenQuotedColorGroup = (group: QuotedColorGroup, { bracketPricingEnabled = false }: BracketPricingOptions = {}): FlatQuotationLine[] =>
   group.quotedColors.flatMap((quotedColor) =>
      quotedColor.quotation.lines.map((line) => {
         const { quantityAmount, quantityUOM, quantityToAmount, palletSizeAmount, netWeightAmount, unitPrice, packaging, packagingGroupId } =
            resolveLineValues(line, bracketPricingEnabled);

         return {
            lineId: line.id,
            lineQuantityAmount: quantityAmount,
            lineQuantityToAmount: quantityToAmount,
            linePalletSizeAmount: palletSizeAmount,
            lineNetWeightAmount: netWeightAmount,
            lineQuantityUOM: quantityUOM,
            lineUnitPrice: unitPrice,
            linePackaging: packaging,
            linePackagingGroupId: packagingGroupId,
            lineBestPrice: line.bestPrice,
            lineRemark: line.remark,
            lineOrderableProduct: line.orderableProduct,
            quotationId: quotedColor.quotation.id,
            quotationName: quotedColor.quotation.name,
            validUntil: quotedColor.quotation.effective.until,
            quoteDescription: quotedColor.quotation.description,
            isPriceFixed: line.isPriceFixed ?? true,
            marketPricingMonth: line.marketPricingMonth
         };
      })
   );

export interface CallOffGroup {
   key: string;
   colorId: number;
   colorName: string;
   producerColorCode: string;
   callOffOrderColor: CallOffOrderColor;
}

export const groupCallOffColors = (callOffOrderColors: CallOffOrderColor[]): CallOffGroup[] => {
   const seen = new Map<string, CallOffGroup>();

   for (const callOff of callOffOrderColors) {
      const key = makeColorGroupKey(callOff.color.id, callOff.color.producerColorCode);

      if (!seen.has(key)) {
         seen.set(key, {
            key,
            colorId: callOff.color.id,
            colorName: callOff.color.name,
            producerColorCode: callOff.color.producerColorCode ?? DEFAULT_PRODUCER_COLOR_CODE,
            callOffOrderColor: callOff
         });
      }
   }

   return Array.from(seen.values());
};

export const getSmallLotPricingForVariant = (prices: SmallLotPricing[], colorId: number, producerColorCode: string): SmallLotPricing | null => {
   const normalizedProducerColorCode = producerColorCode || DEFAULT_PRODUCER_COLOR_CODE;
   return prices.find((pricing) => pricing.color.id === colorId && (pricing.color.producerColorCode || DEFAULT_PRODUCER_COLOR_CODE) === normalizedProducerColorCode) ?? null;
};

export interface UniqueSmallLotColor {
   id: number;
   name: string;
}

export interface SmallLotGroup {
   key: string;
   colorId: number;
   colorName: string;
   producerColorCode: string;
   prices: SmallLotPricing[];
}

export const groupSmallLotPrices = (smallLotPrices: SmallLotPricing[]): SmallLotGroup[] => {
   const seen = new Map<string, SmallLotGroup>();

   for (const smallLotPrice of smallLotPrices) {
      const key = makeColorGroupKey(smallLotPrice.color.id, smallLotPrice.color.producerColorCode);

      if (!seen.has(key)) {
         seen.set(key, {
            key,
            colorId: smallLotPrice.color.id,
            colorName: smallLotPrice.color.name,
            producerColorCode: smallLotPrice.color.producerColorCode ?? DEFAULT_PRODUCER_COLOR_CODE,
            prices: []
         });
      }

      seen.get(key)?.prices.push(smallLotPrice);
   }

   return Array.from(seen.values());
};

export interface SmallLotUrlSelection {
   colorId: number;
   colorCode?: string;
}

interface UrlInitialKeys {
   callOffKey: string | undefined;
   quoteKey: string | undefined;
   smallLotUrlSelection: SmallLotUrlSelection | undefined;
}

const EMPTY_URL_INITIAL_KEYS: UrlInitialKeys = {
   callOffKey: undefined,
   quoteKey: undefined,
   smallLotUrlSelection: undefined
};

const findGroupByColorId = <T extends { key: string; colorId: number }>(
   groups: T[],
   colorId: number,
   colorCode: string | undefined
): T | undefined => groups.find((group) => group.key === makeColorGroupKey(colorId, colorCode)) ?? groups.find((group) => group.colorId === colorId);

const findGroupByColorName = <T extends { key: string; colorName: string; producerColorCode: string }>(
   groups: T[],
   colorName: string,
   producerColorCode: string | undefined
): T | undefined => {
   const normalizedColorName = colorName.toLowerCase();

   return (
      groups.find((group) => group.colorName.toLowerCase() === normalizedColorName && group.producerColorCode === producerColorCode) ??
      groups.find((group) => group.colorName.toLowerCase() === normalizedColorName)
   );
};

const resolveFromIntent = (
   intent: ReturnType<typeof parseProductActionIntent>,
   callOffGroups: CallOffGroup[],
   quotedColorGroups: QuotedColorGroup[]
): UrlInitialKeys | undefined => {
   if (intent.type === 'call-off') {
      const match = findGroupByColorId(callOffGroups, intent.colorId, intent.colorCode);
      return match ? { callOffKey: match.key, quoteKey: undefined, smallLotUrlSelection: undefined } : undefined;
   }

   if (intent.type === 'quote') {
      const match = findGroupByColorId(quotedColorGroups, intent.colorId, intent.colorCode);
      return match ? { callOffKey: undefined, quoteKey: match.key, smallLotUrlSelection: undefined } : undefined;
   }

   if (intent.type === 'small-lot') {
      return { callOffKey: undefined, quoteKey: undefined, smallLotUrlSelection: { colorId: intent.colorId, colorCode: intent.colorCode } };
   }

   return undefined;
};

const resolveFromLegacySchema = (
   from: string | null,
   colorName: string | undefined,
   producerColorCode: string | undefined,
   callOffGroups: CallOffGroup[],
   quotedColorGroups: QuotedColorGroup[]
): UrlInitialKeys | undefined => {
   if (!colorName) return undefined;

   if (from === 'call-off') {
      const match = findGroupByColorName(callOffGroups, colorName, producerColorCode);
      return match ? { callOffKey: match.key, quoteKey: undefined, smallLotUrlSelection: undefined } : undefined;
   }

   if (from === 'quote') {
      const match = findGroupByColorName(quotedColorGroups, colorName, producerColorCode);
      return match ? { callOffKey: undefined, quoteKey: match.key, smallLotUrlSelection: undefined } : undefined;
   }

   return undefined;
};

/**
 * Resolves the initial Order Now selection keys from URL search params.
 * Supports two schemas (new takes priority):
 *   New (M&P cards):           ?from=quote|call-off|small-lot&colorId=X[&colorCode=Y]
 *   Legacy (call-off detail):  ?from=quote|call-off&color=NAME[&producerColorCode=CODE]
 */
export const resolveUrlInitialKeys = (
   searchParams: ReadonlyURLSearchParams | URLSearchParams,
   callOffGroups: CallOffGroup[],
   quotedColorGroups: QuotedColorGroup[]
): UrlInitialKeys => {
   const intent = parseProductActionIntent(searchParams);
   const intentMatch = resolveFromIntent(intent, callOffGroups, quotedColorGroups);
   if (intentMatch) return intentMatch;

   const from = searchParams.get('from');
   const colorName = searchParams.get('color') ?? undefined;
   const legacyProducerColorCode = searchParams.get('producerColorCode') ?? undefined;

   return resolveFromLegacySchema(from, colorName, legacyProducerColorCode, callOffGroups, quotedColorGroups) ?? EMPTY_URL_INITIAL_KEYS;
};
